import { NoSsr } from "@mui/base";
import { useSetAtom } from "jotai";
import type { FunctionComponent, MouseEvent as ReactMouseEvent } from "react";

import { TypeformUtmSource } from "~/components/app-bar/typeform-button/atoms";
import { useIsShowHomepageTypeform } from "~/components/app-bar/typeform-button/hooks";
import { Spacer } from "~/components/common/spacer";
import { hamburgerMenuAtom } from "~/components/hamburger-menu/atoms";
import { DefaultHeaderSwapThreshold } from "~/components/header/consts";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { useEditionValue } from "~/lib/edition";
import { useGetHomeAsPath } from "~/lib/router/hooks";
import { page as HomePage } from "~/pages/home";

import IconMenu from "./header-icon/menu.svg";
import { useHeader, useSwapOnScrollEffect } from "./hooks";
import {
  ActionButton,
  Container,
  StyledDynamicHeaderOnelineMenuContainer,
  StyledLogoFlag,
  StyledLogoLink,
  StyledLogoText,
} from "./styles";
import type { HeaderConfiguration } from "./types";

export type Props = {
  className?: string;
  headerConfiguration?: HeaderConfiguration;
};

export const Header: FunctionComponent<Props> = ({ className, headerConfiguration }) => {
  const edition = useEditionValue();
  const homeAsPath = useGetHomeAsPath();

  const setHamburgerMenuState = useSetAtom(hamburgerMenuAtom);

  const { shouldSwap } = useSwapOnScrollEffect(DefaultHeaderSwapThreshold);
  const { mastheadQueryParameter, renderFeatures, responsiveLogoVariant } = useHeader(
    shouldSwap,
    headerConfiguration,
  );

  const { pathname: homePagePathname, query: homePageQuery } = HomePage.route({
    edition,
    slide: "main",
  });

  const { getShowHomepageTypeformHandler, shouldShowHomepageTypeform } =
    useIsShowHomepageTypeform();
  const onClickHandler = (event: ReactMouseEvent<HTMLAnchorElement, MouseEvent>) => {
    getShowHomepageTypeformHandler(1, TypeformUtmSource.desktop);
    if (shouldShowHomepageTypeform) {
      event.preventDefault();
    }
  };

  return (
    <Container className={className}>
      <StyledLogoLink
        $responsiveVariants={responsiveLogoVariant}
        anchorProps={{ "aria-label": "SCMP homepage" }}
        as={homeAsPath}
        pathname={homePagePathname}
        query={{ ...homePageQuery, ...mastheadQueryParameter }}
        onClick={event => onClickHandler(event)}
      >
        <StyledLogoFlag />
        <StyledLogoText />
      </StyledLogoLink>
      <NoSsr>
        <StyledDynamicHeaderOnelineMenuContainer
          appBarVariant={headerConfiguration?.appBarVariant}
        />
      </NoSsr>
      <Spacer />
      {renderFeatures()}
      <ActionButton
        $responsiveVariants={{
          desktopUp: "hidden",
          largeDesktopUp: "hidden",
          mobileUp: "show",
          tabletUp: "show",
        }}
        onClick={() => {
          setHamburgerMenuState(current => ({
            ...current,
            isOpen: !current.isOpen,
            variant: "default",
          }));
          sendGATracking({
            action: "Menu/Open",
            category: "Menu",
            label: "Menu/Open",
          });
        }}
      >
        <IconMenu />
      </ActionButton>
    </Container>
  );
};

Header.displayName = "Header";
